import React from "react";
import HeaderNavbar from "../Components/BybMainPage/BybNavbar/HeaderNavbar";
import BybFooter from "../Components/BybMainPage/BybFooter/BybFooter";
import PageSeo from "../Components/Common/Seo/PageSeo";
import { SITE_URL, toCanonicalUrl } from "../Components/Common/Seo/seoUtils";
import "./BrandGlossaryPage.css";

const GLOSSARY_TERMS = [
  {
    name: "Brand",
    definition: "A brand is the overall perception people form about a business, product, service, or organization based on its identity, behavior, communication, and customer experience.",
  },
  {
    name: "Branding",
    definition: "Branding is the strategic process of shaping how a business is recognized, remembered, and trusted through consistent positioning, identity, messaging, and experience.",
  },
  {
    name: "Brand Strategy",
    definition: "Brand strategy is the long-term plan that defines what a brand stands for, who it serves, how it is positioned, and how it communicates value in the market.",
  },
  {
    name: "Brand Positioning",
    definition: "Brand positioning is the clear space a brand aims to own in the minds of its target audience compared with competing alternatives.",
  },
  {
    name: "Brand Audit",
    definition: "A brand audit is a structured review of a brand's identity, messaging, customer experience, market position, and consistency to find strengths, gaps, and improvement opportunities.",
  },
  {
    name: "Brand Identity",
    definition: "Brand identity is the set of visible and verbal elements that represent a brand, including its logo, colors, typography, tone, messaging, and design system.",
  },
  {
    name: "Visual Identity",
    definition: "Visual identity is the visual expression of a brand, including logo, color palette, typography, imagery, layout, and other design elements.",
  },
  {
    name: "Verbal Identity",
    definition: "Verbal identity is the language system of a brand, including its name, tagline, tone of voice, key messages, and communication style.",
  },
  {
    name: "Brand Voice",
    definition: "Brand voice is the consistent personality and style a brand uses in its written, spoken, and digital communication.",
  },
  {
    name: "Brand Messaging",
    definition: "Brand messaging is the set of core statements and ideas a brand uses to explain its value, promise, benefits, and difference to its audience.",
  },
  {
    name: "Brand Personality",
    definition: "Brand personality is the human-like character of a brand, expressed through tone, design, behavior, values, and communication.",
  },
  {
    name: "Brand Promise",
    definition: "A brand promise is the expectation a brand sets for customers about the value, experience, or outcome they can consistently expect.",
  },
  {
    name: "Brand Values",
    definition: "Brand values are the core principles and beliefs that guide a brand's decisions, actions, culture, and communication.",
  },
  {
    name: "Target Audience",
    definition: "A target audience is the specific group of people a brand is designed to serve, reach, and influence through its products, services, and communication.",
  },
  {
    name: "Customer Experience",
    definition: "Customer experience is the complete impression a customer forms from every interaction with a brand before, during, and after purchase.",
  },
  {
    name: "Customer Journey",
    definition: "The customer journey is the path a customer takes across touchpoints as they discover, evaluate, buy from, use, and engage with a brand.",
  },
  {
    name: "Differentiation",
    definition: "Differentiation is the meaningful way a brand stands apart from competitors by offering a distinct value, experience, position, or point of view.",
  },
  {
    name: "Competitor Analysis",
    definition: "Competitor analysis is the process of studying competing brands to understand their positioning, strengths, weaknesses, messaging, audience, and market opportunities.",
  },
  {
    name: "Brand Consistency",
    definition: "Brand consistency is the repeated use of the same identity, message, tone, and experience across channels so people can recognize and trust the brand.",
  },
  {
    name: "Brand Equity",
    definition: "Brand equity is the value a brand gains from recognition, trust, loyalty, perceived quality, and positive associations in the minds of customers.",
  },
  {
    name: "Rebranding",
    definition: "Rebranding is the process of changing a brand's identity, positioning, messaging, or experience to better match its strategy, audience, or market direction.",
  },
];

const glossarySchema = {
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  "@id": `${toCanonicalUrl("/brand-glossary")}#definedtermset`,
  name: "Brand Glossary",
  description: "Concise definitions of branding terms including brand positioning, brand audit, brand identity, brand strategy, and customer experience.",
  url: toCanonicalUrl("/brand-glossary"),
  publisher: {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "Brew Your Brand",
  },
  hasDefinedTerm: GLOSSARY_TERMS.map((term) => ({
    "@type": "DefinedTerm",
    name: term.name,
    description: term.definition,
    inDefinedTermSet: `${toCanonicalUrl("/brand-glossary")}#definedtermset`,
  })),
};

const BrandGlossaryPage = () => (
  <>
    <PageSeo
      title="Brand Glossary | Branding Terminology | Brew Your Brand"
      description="A concise branding glossary from Brew Your Brand with definitions for brand positioning, brand audit, brand identity, brand strategy, and customer experience."
      canonical="/brand-glossary"
      ogTitle="Brand Glossary | Brew Your Brand"
      ogDescription="Learn concise definitions of important branding terms including brand positioning, brand audit, brand identity, brand voice, and customer experience."
      structuredData={glossarySchema}
    />
    <HeaderNavbar />
    <main className="brand-glossary-page">
      <section className="brand-glossary-hero">
        <div className="container">
          <p className="brand-glossary-eyebrow">Branding Terminology</p>
          <h1>Brand Glossary</h1>
          <p>
            A concise glossary of branding terms for founders, marketers, MSME teams, and brand builders. Use these definitions to understand brand strategy, audits, positioning, identity, messaging, and customer experience.
          </p>
        </div>
      </section>

      <section className="brand-glossary-list" aria-label="Branding glossary definitions">
        <div className="container">
          <div className="brand-glossary-grid">
            {GLOSSARY_TERMS.map((term) => (
              <article className="brand-glossary-term" key={term.name}>
                <h2>{term.name}</h2>
                <p>{term.definition}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
    <BybFooter />
  </>
);

export default BrandGlossaryPage;